import { Node } from 'reactflow';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Trash2, Check, Mail, Plus, Wand2, CalendarClock, Timer, Zap } from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { toUserFriendlyError } from '@/lib/error-messages';
import EmailSearchPicker, { type PickedEmail } from './EmailSearchPicker';

interface EmailNodeConfigPanelProps {
  node: Node | null;
  onUpdate: (nodeId: string, data: any) => void;
  onDelete?: (nodeId: string) => void;
  onSaveNode?: () => void;
  onOpenEmailEditor?: (emailId: number) => void;
}

export default function EmailNodeConfigPanel({
  node,
  onUpdate,
  onDelete,
  onSaveNode,
  onOpenEmailEditor,
}: EmailNodeConfigPanelProps) {
  if (!node) return null;

  const handleUpdate = (field: string, value: any) => {
    onUpdate(node.id, { ...node.data, [field]: value });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium mb-1.5 block">Nome do Nó</label>
        <Input
          value={node.data.label || ''}
          onChange={(e) => handleUpdate('label', e.target.value)}
          placeholder="Digite um nome para este nó"
          className="text-sm"
        />
      </div>

      <Separator />

      {node.type === 'trigger' && (
        <TriggerConfig node={node} onUpdate={handleUpdate} />
      )}

      {node.type === 'email_content' && (
        <EmailSendConfig node={node} onUpdate={handleUpdate} onOpenEmailEditor={onOpenEmailEditor} />
      )}

      {node.type === 'condition' && (
        <ConditionConfig node={node} onUpdate={handleUpdate} />
      )}

      {node.type === 'delay' && (
        <DelayConfig node={node} onUpdate={handleUpdate} />
      )}

      {node.type === 'action' && (
        <ActionConfig node={node} onUpdate={handleUpdate} />
      )}

      {node.type === 'randomizer' && (
        <div className="space-y-3">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Caminho A — Probabilidade (%)</label>
            <Input
              type="number"
              min="0"
              max="100"
              value={node.data.path1Probability || 50}
              onChange={(e) => handleUpdate('path1Probability', parseInt(e.target.value))}
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Caminho B — Probabilidade (%)</label>
            <div className="text-sm text-muted-foreground bg-muted p-2 rounded-md">
              {100 - (node.data.path1Probability || 50)}%
            </div>
          </div>
        </div>
      )}

      <Separator />
      <div className="flex gap-2">
        {onSaveNode && (
          <Button size="sm" className="flex-1 gap-2" onClick={onSaveNode}>
            <Check className="w-4 h-4" />
            Salvar Nó
          </Button>
        )}
        {/* Trigger não pode ser removido — ele ancora o fluxo. */}
        {onDelete && node.type !== 'trigger' && (
          <Button variant="destructive" size="sm" className="gap-2" onClick={() => onDelete(node.id)}>
            <Trash2 className="w-4 h-4" />
            Remover
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Trigger Config ──────────────────────────────────────────────────────────

function TriggerConfig({ node, onUpdate }: { node: Node; onUpdate: (k: string, v: any) => void }) {
  const data = node.data ?? {};
  const triggerType = (data.triggerType ?? 'manual') as string;

  const { data: tags = [] } = trpc.tags.list.useQuery() as { data?: Array<{ id: number; name: string }> };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 p-3 rounded-lg bg-indigo-50 border border-indigo-200">
        <Zap className="w-5 h-5 text-indigo-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-indigo-900">Gatilho do fluxo</p>
          <p className="text-xs text-indigo-600">
            Define o ponto de entrada — todo o resto sai daqui.
          </p>
        </div>
      </div>

      <div>
        <label className="text-sm font-medium mb-1.5 block">Como esse fluxo dispara?</label>
        <Select value={triggerType} onValueChange={(v) => {
          onUpdate('triggerType', v);
          onUpdate('triggerValue', '');
        }}>
          <SelectTrigger className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="manual">Manual / Campanha</SelectItem>
            <SelectItem value="tag_added">Tag adicionada</SelectItem>
            <SelectItem value="new_contact">Novo contato</SelectItem>
            <SelectItem value="webhook">Webhook (integração externa)</SelectItem>
            <SelectItem value="scheduled">Agendado (data específica)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {triggerType === 'tag_added' && (
        <div>
          <label className="text-sm font-medium mb-1.5 block">Qual tag dispara?</label>
          {tags.length > 0 ? (
            <Select value={data.triggerValue || ''} onValueChange={(v) => onUpdate('triggerValue', v)}>
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Escolha a tag" />
              </SelectTrigger>
              <SelectContent>
                {tags.map((t) => (
                  <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              value={data.triggerValue || ''}
              onChange={(e) => onUpdate('triggerValue', e.target.value)}
              placeholder="nome-da-tag"
              className="text-sm"
            />
          )}
          <p className="text-[11px] text-muted-foreground mt-1">
            Cada contato com essa tag entra no fluxo uma vez.
          </p>
        </div>
      )}

      {triggerType === 'new_contact' && (
        <div className="text-xs text-muted-foreground p-2 rounded border bg-muted/30">
          Esse fluxo dispara para todo novo contato cadastrado no tenant.
          Filtros adicionais (origem, source de cadastro) podem ser adicionados em uma condição logo após o gatilho.
        </div>
      )}

      {triggerType === 'webhook' && (
        <div>
          <label className="text-sm font-medium mb-1.5 block">Endpoint (gerado ao salvar)</label>
          <Input
            value={data.triggerValue || 'Será gerado automaticamente'}
            readOnly
            className="text-sm font-mono bg-muted/40"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Cole esse URL na sua integração externa (formulário, CRM, ESP) para disparar o fluxo.
          </p>
        </div>
      )}

      {triggerType === 'scheduled' && (
        <div>
          <label className="text-sm font-medium mb-1.5 block">Data e hora do disparo</label>
          <Input
            type="datetime-local"
            value={data.triggerValue || ''}
            onChange={(e) => onUpdate('triggerValue', e.target.value)}
            className="text-sm"
          />
        </div>
      )}

      {triggerType === 'manual' && (
        <div className="text-xs text-muted-foreground p-2 rounded border bg-muted/30">
          Você dispara o fluxo manualmente via campanha, CRM ou botão "Disparar agora".
        </div>
      )}

      <div className="text-[11px] text-indigo-700 bg-indigo-50/60 border border-indigo-200/40 rounded p-2 leading-snug">
        💡 <strong>Contexto disponível abaixo:</strong> {' '}
        {triggerType === 'tag_added' && 'contato + tag aplicada + horário'}
        {triggerType === 'new_contact' && 'contato + origem do cadastro'}
        {triggerType === 'webhook' && 'contato (resolvido por e-mail/telefone) + payload do webhook'}
        {triggerType === 'scheduled' && 'data do disparo + segmento alvo'}
        {triggerType === 'manual' && 'contato selecionado na campanha + variáveis do disparo'}
      </div>
    </div>
  );
}

// ─── Email Send Configuration ────────────────────────────────────────────────

function EmailSendConfig({
  node,
  onUpdate,
  onOpenEmailEditor,
}: {
  node: Node;
  onUpdate: (field: string, value: any) => void;
  onOpenEmailEditor?: (id: number) => void;
}) {
  const data = node.data ?? {};
  const utils = trpc.useUtils();

  const createMutation = trpc.emailCampaigns.create.useMutation({
    onSuccess: (created: any) => {
      utils.emailCampaigns.list.invalidate();
      onUpdate('emailId', created.id);
      onUpdate('emailSource', 'campaign');
      onUpdate('selectedEmailName', created.name);
      onUpdate('emailSubject', created.subject ?? '');
      toast.success('E-mail criado. Abrindo o editor…');
      onOpenEmailEditor?.(created.id);
    },
    onError: (err) => toast.error(toUserFriendlyError(err, 'Falha ao criar e-mail')),
  });

  const isConfigured = typeof data.emailId === 'number';
  const selected: PickedEmail | null = isConfigured
    ? {
        source: (data.emailSource as any) ?? 'campaign',
        id: data.emailId,
        name: data.selectedEmailName ?? `E-mail #${data.emailId}`,
        subject: data.emailSubject ?? null,
      }
    : null;

  const handlePick = (picked: PickedEmail) => {
    onUpdate('emailId', picked.id);
    onUpdate('emailSource', picked.source);
    onUpdate('selectedEmailName', picked.name);
    onUpdate('emailSubject', picked.subject ?? '');
    toast.success(`${picked.source === 'campaign' ? 'Campanha' : 'Template'} "${picked.name}" vinculado(a) ao nó`);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 p-3 rounded-lg bg-blue-50 border border-blue-200">
        <Mail className="w-5 h-5 text-blue-600 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-blue-900">Conteúdo do e-mail</p>
          <p className="text-xs text-blue-600">
            {isConfigured
              ? 'Edite o conteúdo no editor (LeadCopy, blocos, preview).'
              : 'Busque um existente ou crie um novo.'}
          </p>
        </div>
        <EmailStatusBadge configured={isConfigured} />
      </div>

      <div>
        <label className="text-sm font-medium mb-1.5 block">E-mail vinculado</label>
        <EmailSearchPicker selected={selected} onPick={handlePick} />
        <p className="text-[11px] text-muted-foreground mt-1.5">
          Busca em <strong>Campanhas</strong> (rascunhos do stepper) e <strong>Templates</strong> (biblioteca reutilizável).
        </p>
      </div>

      {isConfigured ? (
        <div className="grid grid-cols-2 gap-2">
          <Button
            size="sm"
            className="gap-1.5"
            onClick={() => onOpenEmailEditor?.(data.emailId as number)}
          >
            <Wand2 className="w-3.5 h-3.5" />
            Abrir editor
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5"
            onClick={() => {
              onUpdate('emailId', undefined);
              onUpdate('emailSource', undefined);
              onUpdate('selectedEmailName', undefined);
              onUpdate('emailSubject', '');
            }}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Desvincular
          </Button>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <span className="flex-1 h-px bg-border" />
            ou
            <span className="flex-1 h-px bg-border" />
          </div>

          <Button
            size="sm"
            variant="outline"
            className="w-full gap-1.5"
            onClick={() => {
              const name = (node.data.label || 'Novo e-mail').slice(0, 100);
              createMutation.mutate({ name });
            }}
            disabled={createMutation.isPending}
          >
            <Plus className="w-3.5 h-3.5" />
            {createMutation.isPending ? 'Criando…' : 'Criar e-mail novo (abre editor)'}
          </Button>
        </>
      )}
    </div>
  );
}

// ─── Status Badge ────────────────────────────────────────────────────────────

function EmailStatusBadge({ configured }: { configured: boolean }) {
  if (configured) {
    return <Badge variant="default" className="bg-emerald-600 text-[10px]">configurado</Badge>;
  }
  return <Badge variant="outline" className="text-[10px] text-rose-600 border-rose-200">vazio</Badge>;
}

// ─── Delay Config (segundos / minutos / horas / dias / data específica) ─────

function DelayConfig({ node, onUpdate }: { node: Node; onUpdate: (k: string, v: any) => void }) {
  const data = node.data ?? {};
  const mode = (data.delayMode ?? 'duration') as 'duration' | 'until_time';
  const unit = (data.unit ?? 'days') as 'minutes' | 'hours' | 'days' | 'weeks';
  const duration = Number(data.duration ?? 1);

  const presets = [
    { label: '15 min', minutes: 15 },
    { label: '1 h', minutes: 60 },
    { label: '4 h', minutes: 240 },
    { label: '1 dia', minutes: 60 * 24 },
    { label: '3 dias', minutes: 60 * 24 * 3 },
    { label: '7 dias', minutes: 60 * 24 * 7 },
  ];

  const applyPreset = (mins: number) => {
    onUpdate('delayMode', 'duration');
    if (mins < 60) {
      onUpdate('unit', 'minutes');
      onUpdate('duration', mins);
    } else if (mins < 60 * 24) {
      onUpdate('unit', 'hours');
      onUpdate('duration', mins / 60);
    } else {
      onUpdate('unit', 'days');
      onUpdate('duration', mins / (60 * 24));
    }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <Button
          size="sm"
          variant={mode === 'duration' ? 'default' : 'outline'}
          className="gap-1.5"
          onClick={() => onUpdate('delayMode', 'duration')}
        >
          <Timer className="w-3.5 h-3.5" />
          Esperar
        </Button>
        <Button
          size="sm"
          variant={mode === 'until_time' ? 'default' : 'outline'}
          className="gap-1.5"
          onClick={() => onUpdate('delayMode', 'until_time')}
        >
          <CalendarClock className="w-3.5 h-3.5" />
          Em data/hora
        </Button>
      </div>

      {mode === 'duration' ? (
        <>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Quanto tempo esperar</label>
            <div className="flex gap-2">
              <Input
                type="number"
                min={1}
                value={duration}
                onChange={(e) => onUpdate('duration', parseInt(e.target.value || '1'))}
                className="flex-1 text-sm"
              />
              <Select value={unit} onValueChange={(v) => onUpdate('unit', v)}>
                <SelectTrigger className="w-32 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="minutes">Minutos</SelectItem>
                  <SelectItem value="hours">Horas</SelectItem>
                  <SelectItem value="days">Dias</SelectItem>
                  <SelectItem value="weeks">Semanas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <label className="text-xs text-muted-foreground mb-1.5 block">Atalhos</label>
            <div className="flex flex-wrap gap-1.5">
              {presets.map((p) => (
                <Button
                  key={p.label}
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  onClick={() => applyPreset(p.minutes)}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div>
          <label className="text-sm font-medium mb-1.5 block">Esperar até</label>
          <Input
            type="datetime-local"
            value={data.untilTime || ''}
            onChange={(e) => onUpdate('untilTime', e.target.value)}
            className="text-sm"
          />
          <p className="text-[11px] text-muted-foreground mt-1">
            Útil para coordenar com lançamentos, eventos ou janelas de envio específicas.
          </p>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <input
          type="checkbox"
          id="respectOfficeHours"
          checked={!!data.respectOfficeHours}
          onChange={(e) => onUpdate('respectOfficeHours', e.target.checked)}
          className="h-4 w-4"
        />
        <label htmlFor="respectOfficeHours" className="text-xs text-muted-foreground cursor-pointer">
          Respeitar horário comercial (se a janela cair fora, posterga)
        </label>
      </div>
    </div>
  );
}

// ─── Condition Config ────────────────────────────────────────────────────────

function ConditionConfig({ node, onUpdate }: { node: Node; onUpdate: (k: string, v: any) => void }) {
  const conditionType = node.data.conditionType || 'opened';

  return (
    <div className="space-y-3">
      <div>
        <label className="text-sm font-medium mb-1.5 block">Tipo de Condição</label>
        <Select value={conditionType} onValueChange={(v) => onUpdate('conditionType', v)}>
          <SelectTrigger className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="opened">Abriu o e-mail</SelectItem>
            <SelectItem value="clicked">Clicou em um link</SelectItem>
            <SelectItem value="open_count">Abriu N+ vezes</SelectItem>
            <SelectItem value="score">Score de engajamento</SelectItem>
            <SelectItem value="tag">Possui tag</SelectItem>
            <SelectItem value="field">Campo customizado</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {(conditionType === 'tag' || conditionType === 'field') && (
        <div>
          <label className="text-sm font-medium mb-1.5 block">Valor</label>
          <Input
            value={node.data.value || ''}
            onChange={(e) => onUpdate('value', e.target.value)}
            placeholder={conditionType === 'tag' ? 'Nome da tag' : 'Valor do campo'}
            className="text-sm"
          />
        </div>
      )}

      {conditionType === 'clicked' && (
        <div>
          <label className="text-sm font-medium mb-1.5 block">URL específica (opcional)</label>
          <Input
            value={node.data.value || ''}
            onChange={(e) => onUpdate('value', e.target.value)}
            placeholder="Vazio = qualquer link"
            className="text-sm"
          />
        </div>
      )}

      {(conditionType === 'opened' || conditionType === 'clicked' || conditionType === 'open_count') && (
        <div>
          <label className="text-sm font-medium mb-1.5 block">Janela (horas)</label>
          <Input
            type="number"
            min={1}
            value={node.data.windowHours || 24}
            onChange={(e) => onUpdate('windowHours', parseInt(e.target.value))}
            className="text-sm"
          />
        </div>
      )}

      {conditionType === 'open_count' && (
        <div>
          <label className="text-sm font-medium mb-1.5 block">Quantidade mínima</label>
          <Input
            type="number"
            min={1}
            value={node.data.minCount || 2}
            onChange={(e) => onUpdate('minCount', parseInt(e.target.value))}
            className="text-sm"
          />
        </div>
      )}

      {conditionType === 'score' && (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Operador</label>
            <Select value={node.data.scoreOperator || 'gte'} onValueChange={(v) => onUpdate('scoreOperator', v)}>
              <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="gte">≥</SelectItem>
                <SelectItem value="gt">&gt;</SelectItem>
                <SelectItem value="lte">≤</SelectItem>
                <SelectItem value="lt">&lt;</SelectItem>
                <SelectItem value="eq">=</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Valor</label>
            <Input
              type="number"
              value={node.data.scoreValue || 50}
              onChange={(e) => onUpdate('scoreValue', parseInt(e.target.value))}
              className="text-sm"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Action Config ───────────────────────────────────────────────────────────

function ActionConfig({ node, onUpdate }: { node: Node; onUpdate: (k: string, v: any) => void }) {
  const actionType = node.data.actionType || 'add-tag';
  const isEnrollFlow = actionType === 'enroll_in_flow';

  const { data: flowsList = [] } = trpc.flows.list.useQuery(undefined, {
    enabled: isEnrollFlow,
  }) as { data?: Array<{ id: number; name: string; status: string }> };

  return (
    <div className="space-y-3">
      <div>
        <label className="text-sm font-medium mb-1.5 block">Tipo de Ação</label>
        <Select value={actionType} onValueChange={(v) => onUpdate('actionType', v)}>
          <SelectTrigger className="text-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="add-tag">Adicionar Tag</SelectItem>
            <SelectItem value="remove-tag">Remover Tag</SelectItem>
            <SelectItem value="update_score">Adicionar pontos ao Score</SelectItem>
            <SelectItem value="enroll_in_flow">Inscrever em outro Fluxo</SelectItem>
            <SelectItem value="webhook">Enviar Webhook</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isEnrollFlow ? (
        <>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Fluxo destino</label>
            <Select
              value={node.data.targetFlowId?.toString() || ''}
              onValueChange={(v) => onUpdate('targetFlowId', parseInt(v))}
            >
              <SelectTrigger className="text-sm">
                <SelectValue placeholder="Selecione o fluxo destino" />
              </SelectTrigger>
              <SelectContent>
                {flowsList.map((f) => (
                  <SelectItem key={f.id} value={f.id.toString()}>{f.name} · {f.status}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Se já está no fluxo</label>
            <Select
              value={node.data.onAlreadyEnrolled || 'skip'}
              onValueChange={(v) => onUpdate('onAlreadyEnrolled', v)}
            >
              <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="skip">Pular (não duplicar)</SelectItem>
                <SelectItem value="restart">Reiniciar do início</SelectItem>
                <SelectItem value="allow">Permitir duplicata</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </>
      ) : actionType === 'update_score' ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-sm font-medium mb-1.5 block">Pontos</label>
            <Input
              type="number"
              value={node.data.points || 10}
              onChange={(e) => onUpdate('points', parseInt(e.target.value))}
              className="text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-1.5 block">Motivo</label>
            <Input
              value={node.data.reason || ''}
              onChange={(e) => onUpdate('reason', e.target.value)}
              placeholder="ex: abriu_campanha_lancamento"
              className="text-sm"
            />
          </div>
        </div>
      ) : (
        <div>
          <label className="text-sm font-medium mb-1.5 block">Valor</label>
          <Input
            value={node.data.value || ''}
            onChange={(e) => onUpdate('value', e.target.value)}
            placeholder={
              actionType === 'webhook' ? 'https://...' :
              'Nome da tag'
            }
            className="text-sm"
          />
        </div>
      )}
    </div>
  );
}

